import { useEffect, useState, useRef } from 'react';

// https://usehooks.com/useScript/
const cachedScripts: string[] = [];
export default function useScript(src: string, htmlId: string = '', dataAttributes: any = {}) {
  // Keeping track of script loaded and error state
  const [state, setState] = useState({
    loaded: false,
    error: false,
  });

  // If you want to get the latest reference
  // please add and use useLiveRef from web-app.
  // This line here will only get the _first_ passed dataAttributes
  const initialDataAttributes = useRef(dataAttributes).current;

  useEffect(() => {
    // If cachedScripts array already includes src that means another instance ...
    // ... of this hook already loaded this script, so no need to load again.
    if (cachedScripts.includes(src)) {
      setState({
        loaded: true,
        error: false,
      });
    } else {
      cachedScripts.push(src);

      // Create script
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.id = htmlId;

      const dataKeys = Object.keys(initialDataAttributes);
      dataKeys.forEach((key: string) => {
        script.setAttribute(`data-${key}`, initialDataAttributes[key]);
      });

      // Script event listener callbacks for load and error
      const onScriptLoad = () => {
        setState({
          loaded: true,
          error: false,
        });
      };

      const onScriptError = () => {
        // Remove from cachedScripts we can try loading again
        const index = cachedScripts.indexOf(src);
        if (index >= 0) cachedScripts.splice(index, 1);
        script.remove();

        setState({
          loaded: true,
          error: true,
        });
      };

      script.addEventListener('load', onScriptLoad);
      script.addEventListener('error', onScriptError);

      // Add script to document body
      document.body.appendChild(script);

      // Remove event listeners on cleanup
      return () => {
        script.removeEventListener('load', onScriptLoad);
        script.removeEventListener('error', onScriptError);
      };
    }
  }, [src, htmlId, initialDataAttributes]);

  return [state.loaded, state.error];
}
